import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import ProductCalculator from './ProductCalculator';
import { ArrowLeft, Save, User, Mail, Phone, Building, MapPin, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateLeadPage = () => {
  const navigate = useNavigate();
  const { user, token: authToken } = useAuth();
  const { leadId } = useParams();
  
  // Form state
  const [formData, setFormData] = useState({
    nome_cognome: '',
    email: '',
    telefono: '',
    azienda: '',
    piva: '',
    provincia: '',
    indirizzo: '',
    fonte: 'sito_web',
    descrizione_necessita: '',
    note_venditore: ''
  });
  
  const [prodottiVenduti, setProdottiVenduti] = useState([]);
  const [totals, setTotals] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Debug: verifica token
  useEffect(() => {
    console.log('CreateLeadPage - Token presente:', authToken ? 'YES' : 'NO');
    console.log('CreateLeadPage - User:', user);
  }, [authToken, user]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProductsUpdate = (products, calculatedTotals) => {
    setProdottiVenduti(products);
    setTotals(calculatedTotals);
  };

  const handleSave = async () => {
    // Validazione - solo nome_cognome obbligatorio
    if (!formData.nome_cognome) {
      setError('Nome e Cognome è obbligatorio');
      return;
    }

    setSaving(true);
    setError('');

    try {
      
      // 1. Crea il lead
      const leadResponse = await fetch(`${BACKEND_URL}/api/crm/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          valore_stimato: totals?.inflow_totale || 0
        })
      });

      if (!leadResponse.ok) {
        const errorData = await leadResponse.json();
        throw new Error(errorData.detail || 'Errore durante la creazione del lead');
      }

      const leadData = await leadResponse.json();

      // 2. Salva i prodotti associati al lead (da implementare nel backend)
      // Per ora salviamo solo il lead base
      
      setSuccess(`Lead "${formData.nome_cognome}" creato con successo!`);
      setTimeout(() => {
        navigate('/dashboard-sales');
      }, 2000);

    } catch (err) {
      console.error('Errore salvataggio lead:', err);
      setError(err.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 pt-48 pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard-sales')}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Torna alla Dashboard
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">
            {leadId ? 'Modifica Lead' : 'Crea Nuovo Lead'}
          </h1>
          <p className="text-gray-400">
            Inserisci i dati del contatto e aggiungi i prodotti per calcolare INFLOW e pagamento agente
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 mb-6">
            <p className="text-green-400">{success}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Anagrafica Lead */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <User className="w-6 h-6 mr-2 text-blue-400" />
              Anagrafica Lead
            </h2>

            <div className="space-y-4">
              {/* Fonte Lead - PRIMO CAMPO */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fonte Lead
                </label>
                <select
                  name="fonte"
                  value={formData.fonte}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                >
                  <option value="sito_web">Sito Web</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="referral">Referral</option>
                  <option value="telefono">Chiamata Telefonica</option>
                  <option value="customer_base">Cliente Esistente</option>
                </select>
                {formData.fonte === 'customer_base' && (
                  <p className="text-xs text-yellow-400 mt-1">
                    ⚠️ Ricerca cliente esistente - funzionalità in arrivo
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nome e Cognome *
                </label>
                <input
                  type="text"
                  name="nome_cognome"
                  value={formData.nome_cognome}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="Mario Rossi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="mario.rossi@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Telefono
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="+39 333 1234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Azienda
                </label>
                <input
                  type="text"
                  name="azienda"
                  value={formData.azienda}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="Nome Azienda S.r.l."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  P.IVA
                </label>
                <input
                  type="text"
                  name="piva"
                  value={formData.piva}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="12345678901"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Provincia
                </label>
                <input
                  type="text"
                  name="provincia"
                  value={formData.provincia}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="LE"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Indirizzo
                </label>
                <input
                  type="text"
                  name="indirizzo"
                  value={formData.indirizzo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="Via Roma 123, Lecce"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Descrizione Necessità
                </label>
                <textarea
                  name="descrizione_necessita"
                  value={formData.descrizione_necessita}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="Descrivi le esigenze del cliente..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Note Venditore
                </label>
                <textarea
                  name="note_venditore"
                  value={formData.note_venditore}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  placeholder="Note interne..."
                />
              </div>
            </div>
          </div>

          {/* Right Column - Product Calculator */}
          <div>
            {authToken ? (
              <ProductCalculator 
                token={authToken}
                onProductsUpdate={handleProductsUpdate}
                embedded={true}
              />
            ) : (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <p className="text-gray-400">Caricamento...</p>
              </div>
            )}

            {/* Summary Card */}
            {totals && (
              <div className="mt-6 bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-500/50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">💰 Riepilogo Totale Lead</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-300 mb-1">INFLOW TOTALE</p>
                    <p className="text-2xl font-bold text-green-400">€ {totals.inflow_totale.toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-300 mb-1">PAGAMENTO AGENTE</p>
                    <p className="text-2xl font-bold text-blue-400">€ {totals.pagamento_agente_totale.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  {prodottiVenduti.length} prodott{prodottiVenduti.length !== 1 ? 'i' : 'o'} aggiunt{prodottiVenduti.length !== 1 ? 'i' : 'o'}
                </p>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full mt-6 px-6 py-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-bold text-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Save size={24} />
              <span>{saving ? 'Salvataggio...' : 'Salva Lead'}</span>
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateLeadPage;
